import { useTranslate } from "@/lib/i18n";
import { SystemIllustration } from "./SystemIllustration";

/**
 * LOADING — the page-level loading state INSIDE the back-office shell
 * (design: `system-view="loading"`): the pot-on-the-stove card, and nothing
 * else. The sidebar and top bar are the shell's own and stay live around it.
 *
 * There used to be a two-line header skeleton above the card and a seven-row
 * table skeleton below it. Both were guesses at a page that was not there yet:
 * every page has its own header and its own body, so the guess was wrong for
 * most of them and the swap to the real page moved everything. The card is a
 * fixed ~116px (taller only when it wraps on a phone) and starts exactly where
 * a page's first content starts — `ManageShell` draws it inside the same frame
 * a page uses (`pageFrameClass`).
 *
 * WHEN it appears is not this component's business. `ManageShell` shows it only
 * after the previous page has stayed up for 200ms (see `usePageLoadingBox`), so
 * a navigation that resolves quickly never sees it. Per-widget skeletons INSIDE
 * a page (a card still fetching its numbers) are the page's and stay as they
 * are.
 */
export function PageLoading() {
  const t = useTranslate();
  return (
    <div
      data-ui="page-loading"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <div data-ui="page-loading-card">
        <div data-ui="page-loading-art">
          <SystemIllustration art="loading" />
        </div>
        <div data-ui="page-loading-copy">
          <span data-ui="page-loading-title">
            {t((m) => m.systemScreens.loadingTitle)}
          </span>
          <span data-ui="page-loading-body">
            {t((m) => m.systemScreens.loadingBody)}
          </span>
        </div>
        <div data-ui="page-loading-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      </div>
    </div>
  );
}
